import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone, LockOutlined, LoginOutlined, UserOutlined, InfoCircleFilled } from '@ant-design/icons';
import { getFontClass } from '../util';
import LanguageToggle from './languageToggle';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
  username: string;
  setUsername: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  error: string | null;
  apiLoading: boolean;
  onSubmit: () => void;
  onForgotPassword: () => void;
  t: (key: string) => string;
  i18n: { language: string };
}

export const LoginForm: React.FC<LoginFormProps> = ({
  username,
  setUsername,
  password,
  setPassword,
  error,
  apiLoading,
  onSubmit,
  onForgotPassword,
  t,
  i18n,
}) => {
  const navigate = useNavigate();
  const [clickCount, setClickCount] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const textColor = 'text-white';
  const labelColor = 'text-white/80';
  const iconColor = 'text-white/60';
  const buttonClass = '!bg-transparent border-2 border-white text-white hover:!bg-white/10';

  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    };
  }, []);

  const handleVersionClick = () => {
    setClickCount((prev) => prev + 1);

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    clickTimeoutRef.current = setTimeout(() => {
      setClickCount(0);
    }, 500);
  };

  const handleVersionMouseDown = () => {
    if (clickCount >= 2) {
      setIsHolding(true);
      holdTimeoutRef.current = setTimeout(() => {
        navigate('/get-started');
      }, 800);
    }
  };

  const handleVersionMouseUp = () => {
    setIsHolding(false);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
  };
  
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="w-full">
      {/* Header with Logo and Language Toggle */}
      <div dir={i18n.language === 'en' ? 'ltr' : 'ltr'} className="flex justify-between items-center mb-6 sm:mb-8">
        <Typography className={getFontClass(`${textColor} text-3xl sm:text-4xl drop-shadow-lg`, 700)}>
          NeoPaas
        </Typography>
        <div className="flex gap-2">
          <LanguageToggle />
        </div>
      </div>

      {/* Welcome Text */}
      <Typography className={getFontClass(`text-xl sm:text-2xl mb-6 sm:mb-8 ${textColor} font-light`, 500)}>
        {t('loginToYourAccount')}
      </Typography>

      {/* Email Input */}
      <div className="mb-5 sm:mb-6">
        <label htmlFor="username" className={getFontClass(`block mb-2 ${labelColor} text-sm`, 400)}>
          {t('email')}
        </label>
        <Input
          prefix={<UserOutlined className={iconColor} />}
          type="text"
          id="username"
          size="large"
          className="glass-input w-full rounded-xl min-h-[44px]"
          placeholder={t('emailPlaceholder')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onPressEnter={onSubmit}
        />
      </div>

      {/* Password Input */}
      <div className="mb-5 sm:mb-6">
        <label htmlFor="password" className={getFontClass(`block mb-2 ${labelColor} text-sm`, 400)}>
          {t('password')}
        </label>
        <Input.Password
          id="password"
          size="large"
          prefix={<LockOutlined className={iconColor} />}
          className="glass-input w-full rounded-xl min-h-[44px]"
          placeholder={t('passwordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={onSubmit}
          iconRender={(visible) =>
            visible ? <EyeTwoTone twoToneColor="#fff" /> : <EyeInvisibleOutlined className={iconColor} />
          }
        />
      </div>

      {/* Error Message */}
      <div className="min-h-[2rem] mb-4">
        {error && (
          <Typography className={getFontClass('text-red-400 text-center text-xs sm:text-sm bg-red-500/10 py-2 px-3 rounded-lg', 400)}>
            {t(error)}
          </Typography>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 sm:gap-4 mb-5 sm:mb-6">
        <Button
          className={`${buttonClass} w-full min-h-[44px] sm:h-12 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base`}
          size="large"
          iconPosition="end"
          onClick={onSubmit}
          icon={<LoginOutlined />}
          loading={apiLoading}
        >
          {t('login')}
        </Button>

        <Button
          onClick={onForgotPassword}
          size="large"
          className={`${buttonClass} w-full min-h-[44px] sm:h-12 rounded-xl transition-all duration-300 text-sm sm:text-base`}
          iconPosition="start"
          icon={<InfoCircleFilled />}
        >
          {t('forgot')}
        </Button>
      </div>

      {/* Version Code */}
      <div 
        onClick={handleVersionClick}
        onMouseDown={handleVersionMouseDown}
        onMouseUp={handleVersionMouseUp}
        onMouseLeave={handleVersionMouseUp}
        className="mt-4"
      >
        <Typography 
          className={getFontClass(`text-xs text-center transition-all duration-300 cursor-pointer select-none ${isHolding ? 'text-white/80 scale-105' : 'text-white/40'}`, 400)}
        >
          {t('versionCode')}: {__COMMIT_HASH__}
        </Typography>
      </div>
    </form>
  );
};
